/**
 * EntityTreeProcessor - Processa a arvore de entidades e executa API calls na ordem correta.
 *
 * Ordem de dependencia (topological):
 * 1. Professionals (independente)
 * 2. Services (independente)
 * 3. Places (independente)
 * 4. PlaceServices (depende de Place + Service)
 * 5. PlaceProfessionals (depende de Place + Professional)
 * 6. PlaceServiceProfessionals (depende de PlaceService + PlaceProfessional)
 * 7. Schedules (depende de Place + Professional)
 * 8. ScheduleShifts (depende de Schedule)
 */

import professionalsService from './professionalsService';
import servicesService from './servicesService';
import placesService from './placesService';
import placeProfessionalsService from './placeProfessionalsService';
import placeServicesService from './placeServicesService';
import placeServiceProfessionalsService from './placeServiceProfessionalsService';
import schedulesService from './schedulesService';
import { isTempId } from '../hooks/useEntityTree';
import { pluralizeWithCount } from '../utils/pluralize';

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
const API_DELAY_MS = 800;

// Dias da semana para converter shifts
const DAYS_OF_WEEK_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

/**
 * Processa a arvore de entidades criando tudo na API na ordem correta.
 * @param {Object} tree - A arvore de entidades do useEntityTree
 * @param {Function} onProgress - Callback (step, totalSteps, message, status)
 * @returns {Object} idMap - Mapa de tempId -> guid real
 */
export async function processEntityTree(tree, onProgress = () => {}) {
  const idMap = {};  // tempId -> guid real criado
  const apiResults = {
    placeServiceIds: {},    // `${placeId}_${serviceId}` -> placeService.id
    placeProfessionalIds: {}, // `${placeId}_${professionalId}` -> placeProfessional.id
  };

  // Calcular total de operacoes
  const newProfessionals = tree.professionals.filter(p => p.isNew && p.selected !== false);
  const newServices = tree.services.filter(s => s.isNew);
  const newPlaces = tree.places.filter(p => p.isNew);
  const placeServiceLinks = tree.links.placeServices;
  const placeProfessionalLinks = tree.links.placeProfessionals;
  const pspLinks = tree.links.placeServiceProfessionals;
  const newSchedules = tree.schedules.filter(s => !s.isExisting && s.shifts && s.shifts.length > 0);
  const existingSchedulesToUpdate = tree.schedules.filter(s => s.isExisting && s.existingScheduleId);
  const totalShifts = newSchedules.reduce((acc, s) => acc + (s.shifts?.length || 0), 0);

  const totalOps = newProfessionals.length
    + newServices.length
    + newPlaces.length
    + placeServiceLinks.length
    + placeProfessionalLinks.length
    + pspLinks.length
    + newSchedules.length
    + totalShifts
    + existingSchedulesToUpdate.length; // 1 op per existing schedule update

  let completedOps = 0;

  const progress = (message, status = 'running') => {
    completedOps++;
    onProgress(completedOps, totalOps, message, status);
  };

  /** Resolve um ID (tempId vira guid real, guid permanece) */
  const resolveId = (id) => idMap[id] || id;

  try {
    // ==================== FASE 0: Popular cache com IDs de vinculos existentes ====================
    // IDs de link table vem direto da arvore (carregados via GET /api/entities)
    // Nao precisa mais fazer N+1 API calls para resolver

    for (const link of tree.links.placeProfessionals) {
      if (link.id && !isTempId(link.id)) {
        const key = `${link.place}_${link.professional}`;
        apiResults.placeProfessionalIds[key] = link.id;
      }
    }

    for (const link of tree.links.placeServices) {
      if (link.id && !isTempId(link.id)) {
        const key = `${link.place}_${link.service}`;
        apiResults.placeServiceIds[key] = link.id;
      }
    }

    console.log('[EntityTreeProcessor] Cache populado da arvore:', {
      placeProfessionalIds: Object.keys(apiResults.placeProfessionalIds).length,
      placeServiceIds: Object.keys(apiResults.placeServiceIds).length,
    });

    // ==================== FASE 1: Entidades independentes ====================

    // 1a. Criar profissionais novos
    for (const prof of newProfessionals) {
      onProgress(completedOps, totalOps, `Criando profissional "${prof.name}"...`, 'running');
      const created = await professionalsService.create({ name: prof.name });
      idMap[prof.id] = created.id;
      progress(`Profissional "${prof.name}" criado`, 'done');
      await delay(API_DELAY_MS);
    }

    // 1b. Criar servicos novos
    for (const svc of newServices) {
      onProgress(completedOps, totalOps, `Criando servico "${svc.name}"...`, 'running');
      const payload = { name: svc.name };
      if (svc.category) payload.category = svc.category;
      if (svc.duration) payload.duration = svc.duration;
      if (svc.price != null) payload.price = svc.price;
      const created = await servicesService.create(payload);
      idMap[svc.id] = created.id;
      progress(`Servico "${svc.name}" criado`, 'done');
      await delay(API_DELAY_MS);
    }

    // 1c. Criar places novos
    for (const place of newPlaces) {
      onProgress(completedOps, totalOps, `Criando local "${place.name}"...`, 'running');
      const created = await placesService.create({ name: place.name });
      idMap[place.id] = created.id;
      progress(`Local "${place.name}" criado`, 'done');
      await delay(API_DELAY_MS);
    }

    // ==================== FASE 2: Links Place-Service ====================

    for (const link of placeServiceLinks) {
      const placeId = resolveId(link.place);
      const serviceId = resolveId(link.service);
      const key = `${placeId}_${serviceId}`;

      // Pular se link ja existe no banco (ID real vindo da arvore)
      if (link.id && !isTempId(link.id)) {
        apiResults.placeServiceIds[key] = link.id;
        progress(`Servico ja vinculado ao local`, 'done');
        continue;
      }

      const serviceName = tree.services.find(s => s.id === link.service)?.name || 'servico';
      onProgress(completedOps, totalOps, `Vinculando "${serviceName}" ao local...`, 'running');
      try {
        const created = await placeServicesService.create(placeId, { serviceId });
        apiResults.placeServiceIds[key] = created.id || serviceId;
        progress(`"${serviceName}" vinculado ao local`, 'done');
      } catch (err) {
        console.warn('[EntityTreeProcessor] Erro ao vincular servico:', err);
        progress(`"${serviceName}" - ja vinculado ou erro`, 'warning');
      }
      await delay(API_DELAY_MS);
    }

    // ==================== FASE 3: Links Place-Professional ====================

    for (const link of placeProfessionalLinks) {
      const placeId = resolveId(link.place);
      const professionalId = resolveId(link.professional);
      const key = `${placeId}_${professionalId}`;

      // Pular se link ja existe no banco (ID real vindo da arvore)
      if (link.id && !isTempId(link.id)) {
        apiResults.placeProfessionalIds[key] = link.id;
        progress(`Profissional ja vinculado ao local`, 'done');
        continue;
      }

      const profName = tree.professionals.find(p => p.id === link.professional)?.name || 'profissional';
      onProgress(completedOps, totalOps, `Vinculando "${profName}" ao local...`, 'running');
      try {
        const created = await placeProfessionalsService.create(placeId, { professionalId });
        apiResults.placeProfessionalIds[key] = created.id;
        progress(`"${profName}" vinculado ao local`, 'done');
      } catch (err) {
        console.warn('[EntityTreeProcessor] Erro ao vincular profissional:', err);
        progress(`"${profName}" - ja vinculado ou erro`, 'warning');
      }
      await delay(API_DELAY_MS);
    }

    // ==================== FASE 4: Links Place-Service-Professional (3-way) ====================

    for (const link of pspLinks) {
      // Pular se link 3-way ja existe no banco
      if (link.id && !isTempId(link.id)) {
        progress(`Vinculo profissional-servico ja existe`, 'done');
        continue;
      }

      const placeId = resolveId(link.place);
      const serviceId = resolveId(link.service);
      const professionalId = resolveId(link.professional);
      const profName = tree.professionals.find(p => p.id === link.professional)?.name || 'profissional';
      const svcName = tree.services.find(s => s.id === link.service)?.name || 'servico';

      // Precisamos do placeServiceId (nao serviceId) para a rota
      const psKey = `${placeId}_${serviceId}`;
      const placeServiceId = apiResults.placeServiceIds[psKey] || link.placeServiceId || serviceId;

      // Precisamos do placeProfessionalId (nao professionalId) para o body
      const ppKey = `${placeId}_${professionalId}`;
      const placeProfessionalId = apiResults.placeProfessionalIds[ppKey] || link.placeProfessionalId;

      if (!placeProfessionalId) {
        // Este caso NÃO deve acontecer se a árvore foi carregada corretamente com os vínculos existentes
        console.error(`[EntityTreeProcessor] PlaceProfessionalId nao encontrado para ${ppKey}. Arvore mal configurada!`);
        progress(`"${profName}" x "${svcName}" - sem vinculo de profissional`, 'warning');
        continue;
      }

      onProgress(completedOps, totalOps, `Vinculando "${profName}" ao servico "${svcName}"...`, 'running');
      try {
        await placeServiceProfessionalsService.create(placeId, placeServiceId, { placeProfessionalId });
        progress(`"${profName}" vinculado a "${svcName}"`, 'done');
      } catch (err) {
        console.warn('[EntityTreeProcessor] Erro ao vincular prof-service:', err);
        progress(`"${profName}" x "${svcName}" - ja existe ou erro`, 'warning');
      }
      await delay(API_DELAY_MS);
    }

    // ==================== FASE 5a: Atualizar agendas existentes (adicionar servicos) ====================

    for (const schedule of existingSchedulesToUpdate) {
      const profName = tree.professionals.find(p => p.id === schedule.professional)?.name || 'profissional';

      onProgress(completedOps, totalOps, `Atualizando agenda existente de "${profName}"...`, 'running');

      try {
        // Buscar shifts existentes
        const existingShifts = await schedulesService.getShifts(schedule.existingScheduleId);
        const shiftsArray = Array.isArray(existingShifts) ? existingShifts : (existingShifts?.$values || []);
        const activeShifts = shiftsArray.filter(s => s.isActive !== false);

        // Determinar novos serviceIds a adicionar (servicos vinculados ao profissional neste place)
        const newServiceIds = tree.links.placeServiceProfessionals
          .filter(l => l.place === schedule.place && l.professional === schedule.professional)
          .map(l => resolveId(l.service));

        if (newServiceIds.length > 0 && activeShifts.length > 0) {
          for (const shift of activeShifts) {
            const currentServiceIds = (shift.services || shift.Services || []).map(s => s.serviceId || s.ServiceId);
            const mergedServiceIds = [...new Set([...currentServiceIds, ...newServiceIds])];

            // So atualizar se ha novos servicos para adicionar
            if (mergedServiceIds.length > currentServiceIds.length) {
              await schedulesService.updateShift(schedule.existingScheduleId, shift.id, {
                monday: shift.monday,
                tuesday: shift.tuesday,
                wednesday: shift.wednesday,
                thursday: shift.thursday,
                friday: shift.friday,
                saturday: shift.saturday,
                sunday: shift.sunday,
                startTime: shift.startTime,
                endTime: shift.endTime,
                breakBetweenAppointmentsMinutes: shift.breakBetweenAppointmentsMinutes || 0,
                isActive: shift.isActive !== false,
                serviceIds: mergedServiceIds,
              });
              await delay(API_DELAY_MS);
            }
          }
          progress(`${pluralizeWithCount(newServiceIds.length, 'serviço', 'serviços')} adicionado${newServiceIds.length === 1 ? '' : 's'} à agenda de "${profName}"`, 'done');
        } else {
          progress(`Agenda de "${profName}" - sem turnos para atualizar`, 'warning');
        }
      } catch (err) {
        console.warn('[EntityTreeProcessor] Erro ao atualizar agenda existente:', err);
        progress(`Erro ao atualizar agenda de "${profName}"`, 'warning');
      }
    }

    // ==================== FASE 5b: Criar novas Schedules + Shifts ====================

    const createdSchedules = [];

    for (const schedule of newSchedules) {
      const placeId = resolveId(schedule.place);
      const professionalId = resolveId(schedule.professional);
      const profName = tree.professionals.find(p => p.id === schedule.professional)?.name || 'profissional';
      const placeName = tree.places.find(p => p.id === schedule.place)?.name || 'local';
      const scheduleName = schedule.name || `Agenda de ${profName} em ${placeName}`;

      onProgress(completedOps, totalOps, `Criando agenda "${scheduleName}"...`, 'running');
      const created = await schedulesService.create({
        placeId,
        professionalId,
        name: scheduleName,
        maxAttendees: schedule.maxAttendees || 1,  // TODO 2.2: Capacidade da agenda (default 1 = individual)
      });
      createdSchedules.push({ ...created, professionalName: profName, placeName });
      progress(`Agenda "${scheduleName}" criada`, 'done');
      await delay(API_DELAY_MS);

      // Criar shifts
      for (const shift of schedule.shifts) {
        const shiftName = shift.name || `Turno`;

        onProgress(completedOps, totalOps, `Criando turno "${shiftName}"...`, 'running');

        const daysConfig = {};
        DAYS_OF_WEEK_KEYS.forEach(dayKey => {
          daysConfig[dayKey] = shift.days?.includes(dayKey) || false;
        });

        // ✅ Resolver tempIds dos serviceIds para GUIDs reais
        const resolvedServiceIds = (shift.serviceIds || []).map(id => resolveId(id));

        await schedulesService.addShift(created.id, {
          scheduleId: created.id,
          name: shiftName,
          startTime: shift.startTime,
          endTime: shift.endTime,
          ...daysConfig,
          serviceIds: resolvedServiceIds,
        });
        progress(`Turno "${shiftName}" criado`, 'done');
        await delay(API_DELAY_MS);
      }
    }

    return {
      success: true,
      idMap,
      apiResults,
      createdSchedules,
    };

  } catch (error) {
    console.error('[EntityTreeProcessor] Erro fatal:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Erro ao processar',
      idMap,
      apiResults,
      completedOps,
      totalOps,
    };
  }
}

export default processEntityTree;
